import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CostiCantiere } from "@/components/CostiCantiere";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Upload, Loader2, Trash2, ArrowLeft, FileSpreadsheet, Pencil, AlertTriangle, Users, Phone, Mail, Check, X, Sparkles, Link2, Search } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const emptyV = { descrizione: "", unita_misura: "ml", quantita: 0, prezzo_unitario: 0, categoria: "", extra: false, ordine: 0 };
const emptyC = { nome: "", ruolo: "tecnico", telefono: "", email: "", azienda: "", note: "" };
const ruoloLabel = { cliente: "Cliente", tecnico: "Tecnico", geometra: "Geometra", ingegnere: "Ingegnere", architetto: "Architetto", direttore_lavori: "Direttore Lavori", fornitore: "Fornitore", altro: "Altro" };

export default function CantiereDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAdminOrCapo = isAdmin || user?.role === "capocantiere";

  const [cantiere, setCantiere] = useState(null);
  const [avanzamento, setAvanzamento] = useState({ voci: [], totali: {} });
  const [confronto, setConfronto] = useState(null);
  const [contatti, setContatti] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [ddtCantiere, setDdtCantiere] = useState([]);
  const [summary, setSummary] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [selectedVoci, setSelectedVoci] = useState([]);
  const [bulkPct, setBulkPct] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyV);
  const [editing, setEditing] = useState(null);
  const [contattoOpen, setContattoOpen] = useState(false);
  const [contattoForm, setContattoForm] = useState(emptyC);
  const [ocrBusy, setOcrBusy] = useState(false);
  const fileRef = useRef();
  // Link manuale fatture/DDT al cantiere
  const [linkFattureOpen, setLinkFattureOpen] = useState(false);
  const [linkDDTOpen, setLinkDDTOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkSelectedIds, setLinkSelectedIds] = useState([]);
  const [linkFattureAll, setLinkFattureAll] = useState([]);
  const [linkDDTAll, setLinkDDTAll] = useState([]);

  const openLinkModal = async (kind) => {
    setLinkSelectedIds([]); setLinkSearch("");
    setLinkBusy(true);
    try {
      if (kind === "fattura") {
        const { data } = await api.get("/fatture");
        setLinkFattureAll(data || []);
        setLinkFattureOpen(true);
      } else {
        const { data } = await api.get("/ddt");
        setLinkDDTAll(data || []);
        setLinkDDTOpen(true);
      }
    } catch { toast.error("Errore caricamento documenti"); }
    finally { setLinkBusy(false); }
  };

  const confirmLink = async (kind) => {
    if (linkSelectedIds.length === 0) return toast.error("Seleziona almeno un documento");
    setLinkBusy(true);
    try {
      const body = kind === "fattura" ? { fattura_ids: linkSelectedIds } : { ddt_ids: linkSelectedIds };
      const { data } = await api.post(`/cantieri/${id}/link-documents`, body);
      const n = kind === "fattura" ? data.updated_fatture : data.updated_ddt;
      toast.success(`${n} ${kind === "fattura" ? "fatture" : "DDT"} collegate al cantiere`);
      setLinkFattureOpen(false); setLinkDDTOpen(false);
      setLinkSelectedIds([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore collegamento"); }
    finally { setLinkBusy(false); }
  };
  const toggleLink = (docId) => setLinkSelectedIds((s) => s.includes(docId) ? s.filter((x) => x !== docId) : [...s, docId]);
  const filterLinkable = (docs, isDdt) => {
    const q = linkSearch.toLowerCase().trim();
    const current = (docs || []).filter((d) => d.id && d.cantiere_id !== id); // escludi quelli già di questo cantiere
    const sorted = [...current].sort((a, b) => {
      // Prioritizza i null-cantiere
      const au = !a.cantiere_id ? 0 : 1;
      const bu = !b.cantiere_id ? 0 : 1;
      if (au !== bu) return au - bu;
      return String(b.data || "").localeCompare(String(a.data || ""));
    });
    if (!q) return sorted;
    return sorted.filter((d) => {
      const parts = [d.numero || "", d.controparte || "", d.descrizione || "", d.data || ""].map((x) => String(x).toLowerCase());
      return parts.some((p) => p.includes(q));
    });
  };

  const load = async () => {
    try {
      const base = [
        api.get("/cantieri"),
        api.get(`/cantieri/${id}/avanzamento-opere`),
        api.get(`/cantieri/${id}/summary`).catch(() => ({ data: null })),
      ];
      if (isAdmin) {
        base.push(api.get(`/cantieri/${id}/contatti`));
        base.push(api.get(`/fatture`, { params: { cantiere_id: id } }));
        base.push(api.get(`/ddt`, { params: { cantiere_id: id } }));
      }
      const results = await Promise.all(base);
      const [c, a, s, co, ff, dd] = results;
      setCantiere(c.data.find((x) => x.id === id));
      setAvanzamento(a.data);
      setSummary(s?.data || null);
      setContatti(isAdmin && co ? co.data : []);
      setFatture(isAdmin && ff ? ff.data : []);
      setDdtCantiere(isAdmin && dd ? dd.data : []);
      if (isAdminOrCapo) {
        try { const { data } = await api.get(`/cantieri/${id}/confronto`); setConfronto(data); } catch {}
      }
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveVoce = async () => {
    try {
      const payload = { ...form, quantita: Number(form.quantita), prezzo_unitario: Number(form.prezzo_unitario) };
      if (editing) await api.put(`/cantieri/${id}/voci/${editing}`, payload);
      else await api.post(`/cantieri/${id}/voci`, payload);
      toast.success("Voce salvata"); setForm(emptyV); setEditing(null); setOpen(false); load();
    } catch { toast.error("Errore"); }
  };
  const removeVoce = async (vid) => { if (window.confirm("Eliminare la voce?")) { try { await api.delete(`/cantieri/${id}/voci/${vid}`); toast.success("Eliminata"); load(); } catch { toast.error("Errore"); } } };
  const setVocePct = async (vid, pct) => { try { await api.patch(`/cantieri/${id}/voci/${vid}/avanzamento`, { percentuale: pct }); toast.success(`Avanzamento aggiornato a ${pct}%`); load(); } catch { toast.error("Errore"); } };
  const editVoce = (v) => { setEditing(v.id); setForm({ descrizione: v.descrizione || "", unita_misura: v.unita_misura || "ml", quantita: v.quantita_prevista ?? 0, prezzo_unitario: v.prezzo_unitario ?? 0, categoria: v.categoria || "", extra: v.extra || false, ordine: 0 }); setOpen(true); };
  const addExtra = () => { setEditing(null); setForm({ ...emptyV, extra: true, categoria: "Opere extra" }); setOpen(true); };
  const uploadComputo = async (file) => {
    if (!file) return;
    setOcrBusy(true); toast.info("Analisi AI del computo (30s)...");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/cantieri/${id}/voci/ocr`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} voci caricate`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "OCR fallito"); }
    finally { setOcrBusy(false); }
  };

  const saveContatto = async () => {
    try { await api.post(`/cantieri/${id}/contatti`, contattoForm); toast.success("Contatto aggiunto"); setContattoForm(emptyC); setContattoOpen(false); load(); }
    catch { toast.error("Errore"); }
  };
  const removeContatto = async (cid) => { if (window.confirm("Elimina contatto?")) { try { await api.delete(`/cantieri/${id}/contatti/${cid}`); toast.success("Rimosso"); load(); } catch { toast.error("Errore"); } } };

  const setTelefonoCliente = async (telefono) => {
    try {
      const payload = { ...cantiere, telefono_cliente: telefono };
      delete payload.id; delete payload.created_at; delete payload.created_by; delete payload.avanzamento_history;
      await api.put(`/cantieri/${id}`, payload);
      toast.success("Telefono cliente aggiornato"); load();
    } catch { toast.error("Errore"); }
  };

  if (!cantiere) return <div className="text-muted-foreground">Caricamento...</div>;
  const voci = avanzamento.voci || [];
  const voci_base = voci.filter((v) => !v.extra);
  const voci_extra = voci.filter((v) => v.extra);
  const tot = avanzamento.totali || {};
  const inPerdita = (confronto?.voci || []).filter((v) => v.in_perdita);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => nav("/cantieri")}><ArrowLeft className="h-4 w-4 mr-1" /> Cantieri</Button>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Cantiere</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{cantiere.nome}</h1>
          <div className="text-sm text-muted-foreground">{cantiere.cliente} · {cantiere.indirizzo}</div>
        </div>
        {isAdminOrCapo && (
          <a href={`${API}/pdf/cantiere-avanzamento/${id}`} target="_blank" rel="noreferrer" data-testid="sal-pdf-btn">
            <Button variant="outline" className="h-10"><FileSpreadsheet className="h-4 w-4 mr-2" /> SAL PDF</Button>
          </a>
        )}
        {isAdmin && (
          <Button
            data-testid="ai-summary-btn"
            variant="outline"
            className="h-10"
            disabled={aiBusy}
            onClick={async () => {
              setAiBusy(true); setAiSummary(null);
              try {
                const { data } = await api.get(`/ai/cantiere-summary/${id}`, { timeout: 60000 });
                setAiSummary(data);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Errore AI");
              } finally { setAiBusy(false); }
            }}
          >
            {aiBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2 text-primary" />}
            Riassunto AI
          </Button>
        )}
      </div>

      {isAdmin && Object.keys(tot).length > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valore computo</div><div className="text-2xl font-extrabold">{fmt(tot.importo_previsto)}</div></CardContent></Card>
          <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Realizzato</div><div className="text-2xl font-extrabold text-emerald-600">{fmt(tot.importo_realizzato)}</div></CardContent></Card>
          <Card className="card-elevated"><CardContent className="p-5"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Residuo</div><div className="text-2xl font-extrabold text-primary">{fmt((tot.importo_previsto || 0) - (tot.importo_realizzato || 0))}</div></CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="opere">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="opere" data-testid="tab-opere">Opere ({voci_base.length})</TabsTrigger>
          <TabsTrigger value="extra" data-testid="tab-extra">Extra ({voci_extra.length})</TabsTrigger>
          {isAdminOrCapo && <TabsTrigger value="confronto" data-testid="tab-confronto">Confronto {inPerdita.length > 0 && <Badge variant="destructive" className="ml-2 text-xs">{inPerdita.length}</Badge>}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="fatture" data-testid="tab-fatture">Fatture & DDT ({fatture.length + ddtCantiere.length})</TabsTrigger>}
          {isAdmin && <TabsTrigger value="costi" data-testid="tab-costi">Costi</TabsTrigger>}
          {isAdmin && <TabsTrigger value="rubrica" data-testid="tab-rubrica">Contatti utili ({contatti.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="opere" className="space-y-4 pt-4">
          {isAdmin && (
            <div className="flex flex-wrap gap-2 items-center">
              {selectedVoci.length > 0 && (
                <div className="flex items-center gap-2 bg-secondary rounded-lg px-3 py-1.5 text-xs">
                  <span className="font-bold" data-testid="voci-sel-count">{selectedVoci.length} selezionate</span>
                  {isAdmin && (
                    <>
                      <Input data-testid="voci-bulk-pct" type="number" min="0" max="100" step="1" placeholder="% a tutte"
                             value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} className="h-7 w-24 text-xs" />
                      <Button data-testid="voci-bulk-apply" size="sm" variant="outline" disabled={!bulkPct}
                              onClick={async () => {
                                const n = Math.max(0, Math.min(100, Number(bulkPct)));
                                for (const vid of selectedVoci) { await api.patch(`/cantieri/${id}/voci/${vid}/avanzamento`, { percentuale: n }); }
                                toast.success(`${selectedVoci.length} voci aggiornate al ${n}%`);
                                setSelectedVoci([]); setBulkPct(""); load();
                              }}>Applica %</Button>
                      <Button data-testid="voci-bulk-del" size="sm" variant="destructive"
                              onClick={async () => {
                                if (!window.confirm(`Eliminare ${selectedVoci.length} voci?`)) return;
                                for (const vid of selectedVoci) { try { await api.delete(`/cantieri/${id}/voci/${vid}`); } catch {} }
                                toast.success(`${selectedVoci.length} voci eliminate`);
                                setSelectedVoci([]); load();
                              }}>Elimina</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setSelectedVoci([])}>Annulla selezione</Button>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadComputo(e.target.files?.[0])} data-testid="computo-file" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={ocrBusy} data-testid="computo-upload">
                {ocrBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Carica computo (PDF/foto)
              </Button>
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyV); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-voce" onClick={() => { setEditing(null); setForm(emptyV); }}><Plus className="h-4 w-4 mr-2" /> Aggiungi voce</Button>
                </DialogTrigger>
                <DialogContent className="max-w-xl">
                  <DialogHeader><DialogTitle>{editing ? "Modifica voce" : form.extra ? "Nuova opera extra" : "Nuova voce"}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Descrizione</Label><Textarea rows={2} data-testid="v-desc" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>UM</Label><Select value={form.unita_misura} onValueChange={(v) => setForm({ ...form, unita_misura: v })}><SelectTrigger data-testid="v-um"><SelectValue /></SelectTrigger><SelectContent>{["ml", "mq", "mc", "cad", "h", "kg", "corpo"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label>Quantità</Label><Input data-testid="v-qta" type="number" step="0.01" value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} /></div>
                      {isAdmin && <div><Label>€/UM</Label><Input data-testid="v-prezzo" type="number" step="0.01" value={form.prezzo_unitario} onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} /></div>}
                    </div>
                    <div><Label>Categoria</Label><Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} /></div>
                    {isAdmin && <div className="bg-secondary rounded-lg p-3 flex justify-between"><span>Importo</span><span className="font-bold text-primary">{fmt(Number(form.quantita) * Number(form.prezzo_unitario))}</span></div>}
                  </div>
                  <Button data-testid="v-save" onClick={saveVoce} className="h-11 mt-2">Salva</Button>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <VociTable voci={voci_base} isAdmin={isAdmin} isAdminOrCapo={isAdminOrCapo} onEdit={editVoce} onDelete={removeVoce} onSetPct={setVocePct} selected={selectedVoci} onToggle={(vid) => setSelectedVoci((s) => s.includes(vid) ? s.filter((x) => x !== vid) : [...s, vid])} onSetFatto={async (vid, qta, prev) => { if (!(prev > 0)) return; const pct = Math.max(0, Math.min(100, (Number(qta) / prev) * 100)); await setVocePct(vid, pct); }} />
        </TabsContent>

        <TabsContent value="extra" className="space-y-4 pt-4">
          {isAdmin && <Button data-testid="add-extra" onClick={addExtra}><Plus className="h-4 w-4 mr-2" /> Aggiungi opera extra</Button>}
          <VociTable voci={voci_extra} isAdmin={isAdmin} isAdminOrCapo={isAdminOrCapo} onEdit={editVoce} onDelete={removeVoce} onSetPct={setVocePct} selected={selectedVoci} onToggle={(vid) => setSelectedVoci((s) => s.includes(vid) ? s.filter((x) => x !== vid) : [...s, vid])} onSetFatto={async (vid, qta, prev) => { if (!(prev > 0)) return; const pct = Math.max(0, Math.min(100, (Number(qta) / prev) * 100)); await setVocePct(vid, pct); }} />
        </TabsContent>

        <TabsContent value="confronto" className="space-y-4 pt-4">
          {!confronto || confronto.voci.length === 0 ? (
            <Card className="card-elevated"><CardContent className="p-10 text-center text-muted-foreground">Nessuna voce da confrontare.</CardContent></Card>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className={`card-elevated ${inPerdita.length > 0 ? "border-destructive" : "border-emerald-600"}`}>
                  <CardContent className="p-5">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Voci in perdita</div>
                    <div className={`text-3xl font-extrabold ${inPerdita.length > 0 ? "text-destructive" : "text-emerald-700"}`}>{confronto.n_in_perdita}</div>
                    <div className="text-xs text-muted-foreground mt-1">voci dove hai realizzato più del previsto</div>
                  </CardContent>
                </Card>
                {isAdmin && (
                  <Card className="card-elevated">
                    <CardContent className="p-5">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Scostamento totale (€)</div>
                      <div className={`text-3xl font-extrabold ${(confronto.scostamento_totale_euro || 0) > 0 ? "text-destructive" : "text-emerald-700"}`}>{fmt(confronto.scostamento_totale_euro || 0)}</div>
                      <div className="text-xs text-muted-foreground mt-1">valore eccedente il computo originario</div>
                    </CardContent>
                  </Card>
                )}
              </div>
              <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Previsto</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Fatto</th>
                      <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Scostamento</th>
                      {isAdmin && <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">€ Scostamento</th>}
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {confronto.voci.map((v) => (
                      <tr key={v.id} className={`border-t border-border ${v.in_perdita ? "bg-destructive/5" : ""}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium">{v.descrizione}</div>
                          {v.extra && <Badge variant="outline" className="text-[9px]">extra</Badge>}
                        </td>
                        <td className="px-4 py-3 tabular-nums">{v.quantita_prevista} {v.unita_misura}</td>
                        <td className="px-4 py-3 tabular-nums font-semibold">{v.quantita_fatta} {v.unita_misura}</td>
                        <td className={`px-4 py-3 tabular-nums font-bold ${v.scostamento_quantita > 0 ? "text-destructive" : "text-muted-foreground"}`}>
                          {v.scostamento_quantita > 0 ? "+" : ""}{v.scostamento_quantita} {v.unita_misura}
                        </td>
                        {isAdmin && (
                          <td className={`px-4 py-3 tabular-nums font-bold ${v.scostamento_euro > 0 ? "text-destructive" : "text-muted-foreground"}`}>
                            {v.scostamento_euro > 0 ? "+" : ""}{fmt(v.scostamento_euro)}
                          </td>
                        )}
                        <td className="px-4 py-3">{v.in_perdita && <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />Perdita</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="fatture" className="space-y-4 pt-4">
          {isAdmin && (
            <>
              {summary && (
                <div className="grid md:grid-cols-4 gap-3">
                  <Card className="card-elevated"><CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fatturato (incl. IVA)</div>
                    <div className="text-xl font-extrabold text-emerald-600">{fmt(summary.ricavi_totali)}</div>
                  </CardContent></Card>
                  <Card className="card-elevated"><CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Costi fatturati</div>
                    <div className="text-xl font-extrabold text-red-600">{fmt(summary.costi_totali)}</div>
                  </CardContent></Card>
                  <Card className="card-elevated"><CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">DDT aperti ({summary.n_ddt_aperti})</div>
                    <div className="text-xl font-extrabold text-amber-600">{fmt(summary.costi_stimati_pendenti)}</div>
                    <div className="text-[10px] text-muted-foreground">in attesa di fattura fornitore</div>
                  </CardContent></Card>
                  <Card className="card-elevated"><CardContent className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Margine</div>
                    <div className={`text-xl font-extrabold ${summary.margine >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(summary.margine)}</div>
                    <div className="text-[10px] text-muted-foreground">incl. manodopera {fmt(summary.manodopera_totale)} · altri costi {fmt(summary.altri_costi)}</div>
                  </CardContent></Card>
                </div>
              )}

              <div>
                <div className="text-sm font-bold mb-2 flex items-center justify-between gap-2 flex-wrap">
                  <span>Fatture di questo cantiere ({fatture.length})</span>
                  <Button size="sm" data-testid="link-fattura-btn" onClick={() => openLinkModal("fattura")} className="h-8 text-xs">
                    <Link2 className="h-3 w-3 mr-1" /> Collega Fattura
                  </Button>
                </div>
                {fatture.length === 0 ? (
                  <Card className="card-elevated"><CardContent className="p-6 text-center text-muted-foreground text-sm">Nessuna fattura collegata.</CardContent></Card>
                ) : (
                  <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary"><tr className="text-left">
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Data</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Tipo</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">N°</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Controparte</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Totale</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Stato</th>
                      </tr></thead>
                      <tbody>
                        {fatture.map((f) => (
                          <tr key={f.id} className="border-t border-border" data-testid={`cantiere-fattura-${f.id}`}>
                            <td className="px-4 py-2">{f.data}</td>
                            <td className="px-4 py-2"><Badge className={f.tipo === "vendita" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{f.tipo}</Badge></td>
                            <td className="px-4 py-2">{f.numero || "—"}</td>
                            <td className="px-4 py-2 font-medium">{f.controparte}</td>
                            <td className="px-4 py-2 tabular-nums font-bold">{fmt(f.totale)}</td>
                            <td className="px-4 py-2"><Badge variant={f.stato === "pagata" ? "default" : "destructive"} className="text-[10px]">{f.stato}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent></Card>
                )}
              </div>

              <div>
                <div className="text-sm font-bold mb-2 flex items-center justify-between gap-2 flex-wrap">
                  <span>DDT di questo cantiere ({ddtCantiere.length})</span>
                  <Button size="sm" data-testid="link-ddt-btn" onClick={() => openLinkModal("ddt")} className="h-8 text-xs">
                    <Link2 className="h-3 w-3 mr-1" /> Collega DDT
                  </Button>
                </div>
                {ddtCantiere.length === 0 ? (
                  <Card className="card-elevated"><CardContent className="p-6 text-center text-muted-foreground text-sm">Nessun DDT.</CardContent></Card>
                ) : (
                  <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary"><tr className="text-left">
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Data</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">N°</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Descrizione</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Stima €</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Assegnato €</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Fatturazione</th>
                        <th className="px-4 py-2 text-[10px] uppercase text-muted-foreground">Pagamento</th>
                      </tr></thead>
                      <tbody>
                        {ddtCantiere.map((d) => {
                          const effPag = d.stato_pagamento_effettivo || d.stato_pagamento || "non_pagato";
                          const pagColor = effPag === "pagato" ? "bg-emerald-100 text-emerald-800" : effPag === "parziale" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
                          const pagLabel = effPag === "pagato" ? "Pagato" : effPag === "parziale" ? "Parziale" : "Non pagato";
                          return (
                          <tr key={d.id} className="border-t border-border" data-testid={`cantiere-ddt-${d.id}`}>
                            <td className="px-4 py-2">{d.data}</td>
                            <td className="px-4 py-2">{d.numero || "s.n."}</td>
                            <td className="px-4 py-2 text-xs truncate max-w-[280px]">{d.descrizione || "—"}</td>
                            <td className="px-4 py-2 tabular-nums text-muted-foreground">{fmt(d.importo_stimato || 0)}</td>
                            <td className="px-4 py-2 tabular-nums font-semibold">{fmt(d.importo_assegnato || 0)}</td>
                            <td className="px-4 py-2">
                              {d.fattura_id
                                ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">fatturato</Badge>
                                : <Badge variant="outline" className="text-[10px]">in attesa fattura</Badge>}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-1 flex-wrap">
                                <Badge className={`text-[10px] ${pagColor}`}>{pagLabel}</Badge>
                                {d.ereditato && d.fattura_numero && (
                                  <Badge variant="outline" className="text-[9px] border-sky-400 text-sky-700" title={`Ereditato da fattura ${d.fattura_numero}`}>ered. #{d.fattura_numero}</Badge>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent></Card>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="costi" className="pt-4">
            <CostiCantiere cantiereId={id} onChanged={load} />
          </TabsContent>
        )}

        <TabsContent value="rubrica" className="space-y-4 pt-4">
          {isAdmin && (
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <Label>Telefono cliente (per promemoria WhatsApp)</Label>
                <div className="flex gap-2">
                  <Input data-testid="tel-cliente" defaultValue={cantiere.telefono_cliente || ""} placeholder="+39 333 1234567" onBlur={(e) => e.target.value !== (cantiere.telefono_cliente || "") && setTelefonoCliente(e.target.value)} />
                </div>
              </div>
              <Dialog open={contattoOpen} onOpenChange={setContattoOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-contatto"><Plus className="h-4 w-4 mr-2" /> Nuovo contatto</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Aggiungi contatto</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Nome</Label><Input data-testid="c-nome" value={contattoForm.nome} onChange={(e) => setContattoForm({ ...contattoForm, nome: e.target.value })} /></div>
                    <div>
                      <Label>Ruolo</Label>
                      <Select value={contattoForm.ruolo} onValueChange={(v) => setContattoForm({ ...contattoForm, ruolo: v })}>
                        <SelectTrigger data-testid="c-ruolo"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(ruoloLabel).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Azienda</Label><Input value={contattoForm.azienda} onChange={(e) => setContattoForm({ ...contattoForm, azienda: e.target.value })} /></div>
                    <div><Label>Telefono</Label><Input data-testid="c-tel" value={contattoForm.telefono} onChange={(e) => setContattoForm({ ...contattoForm, telefono: e.target.value })} /></div>
                    <div><Label>Email</Label><Input value={contattoForm.email} onChange={(e) => setContattoForm({ ...contattoForm, email: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={contattoForm.note} onChange={(e) => setContattoForm({ ...contattoForm, note: e.target.value })} /></div>
                  </div>
                  <Button data-testid="c-save" onClick={saveContatto} className="h-11 mt-2">Salva</Button>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {contatti.length === 0 ? (
            <Card className="card-elevated"><CardContent className="p-10 text-center text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" /> Nessun contatto. Aggiungi tecnici, geometri, ingegneri.
            </CardContent></Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {contatti.map((c) => (
                <Card key={c.id} className="card-elevated">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold">{c.nome}</div>
                        <Badge variant="secondary" className="badge-tag mt-1">{ruoloLabel[c.ruolo] || c.ruolo}</Badge>
                        {c.azienda && <div className="text-xs text-muted-foreground mt-1">{c.azienda}</div>}
                      </div>
                      {isAdmin && <Button variant="ghost" size="icon" onClick={() => removeContatto(c.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>}
                    </div>
                    {c.telefono && (
                      <a href={`tel:${c.telefono}`} className="flex items-center gap-2 text-sm hover:text-primary">
                        <Phone className="h-3 w-3" /> {c.telefono}
                      </a>
                    )}
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-sm hover:text-primary">
                        <Mail className="h-3 w-3" /> {c.email}
                      </a>
                    )}
                    {c.note && <div className="text-xs text-muted-foreground pt-1 border-t border-border">{c.note}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!aiSummary} onOpenChange={(o) => !o && setAiSummary(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Riassunto AI del cantiere
            </DialogTitle>
          </DialogHeader>
          {aiSummary && (
            <div className="space-y-3">
              <div className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="ai-summary-text">
                {aiSummary.summary}
              </div>
              <div className="text-[10px] text-muted-foreground text-right">
                Generato con Gemini 2.5 · {new Date(aiSummary.generated_at).toLocaleString("it-IT")}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modale Collega Fattura */}
      <Dialog open={linkFattureOpen} onOpenChange={setLinkFattureOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Collega fatture esistenti a {cantiere?.nome || "questo cantiere"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input data-testid="link-fattura-search" placeholder="Cerca per controparte, numero o data..." value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} className="pl-8 h-9" />
            </div>
            <div className="text-[11px] text-muted-foreground">
              {linkSelectedIds.length} selezionate · Le fatture senza cantiere sono in cima (badge arancio).
            </div>
            <div className="max-h-[55vh] overflow-y-auto border rounded-lg">
              {filterLinkable(linkFattureAll, false).length === 0 && <div className="p-6 text-center text-muted-foreground text-sm">Nessuna fattura trovata.</div>}
              {filterLinkable(linkFattureAll, false).map((f) => {
                const checked = linkSelectedIds.includes(f.id);
                const unlinked = !f.cantiere_id;
                return (
                  <label key={f.id} data-testid={`link-fattura-row-${f.id}`}
                         className={`flex items-center gap-3 p-3 border-b border-border cursor-pointer hover:bg-secondary/50 ${checked ? "bg-primary/5" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleLink(f.id)} className="accent-primary" data-testid={`link-fattura-check-${f.id}`} />
                    {unlinked && <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 uppercase">no cantiere</Badge>}
                    <Badge className={`text-[9px] ${f.tipo === "vendita" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>{f.tipo}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{f.controparte || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">N° {f.numero || "—"} · {f.data} · {fmt(f.totale)}</div>
                    </div>
                    {f.cantiere_id && f.cantiere_id !== id && <Badge variant="outline" className="text-[9px]">altro cantiere</Badge>}
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setLinkFattureOpen(false)}>Annulla</Button>
              <Button className="flex-1" data-testid="link-fattura-confirm" onClick={() => confirmLink("fattura")} disabled={linkBusy || linkSelectedIds.length === 0}>
                {linkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                Collega {linkSelectedIds.length > 0 ? `(${linkSelectedIds.length})` : ""}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale Collega DDT */}
      <Dialog open={linkDDTOpen} onOpenChange={setLinkDDTOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Collega DDT esistenti a {cantiere?.nome || "questo cantiere"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input data-testid="link-ddt-search" placeholder="Cerca per fornitore, numero o descrizione..." value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} className="pl-8 h-9" />
            </div>
            <div className="text-[11px] text-muted-foreground">
              {linkSelectedIds.length} selezionati · I DDT senza cantiere sono in cima (badge arancio).
            </div>
            <div className="max-h-[55vh] overflow-y-auto border rounded-lg">
              {filterLinkable(linkDDTAll, true).length === 0 && <div className="p-6 text-center text-muted-foreground text-sm">Nessun DDT trovato.</div>}
              {filterLinkable(linkDDTAll, true).map((d) => {
                const checked = linkSelectedIds.includes(d.id);
                const unlinked = !d.cantiere_id;
                return (
                  <label key={d.id} data-testid={`link-ddt-row-${d.id}`}
                         className={`flex items-center gap-3 p-3 border-b border-border cursor-pointer hover:bg-secondary/50 ${checked ? "bg-primary/5" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleLink(d.id)} className="accent-primary" data-testid={`link-ddt-check-${d.id}`} />
                    {unlinked && <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 uppercase">no cantiere</Badge>}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{d.controparte || d.fornitore_nome || "—"}</div>
                      <div className="text-[10px] text-muted-foreground truncate">N° {d.numero || "s.n."} · {d.data} · {fmt(d.importo_stimato || 0)} · {d.descrizione || ""}</div>
                    </div>
                    {d.fattura_id && <Badge className="bg-emerald-100 text-emerald-800 text-[9px]">fatturato</Badge>}
                    {d.cantiere_id && d.cantiere_id !== id && <Badge variant="outline" className="text-[9px]">altro cantiere</Badge>}
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setLinkDDTOpen(false)}>Annulla</Button>
              <Button className="flex-1" data-testid="link-ddt-confirm" onClick={() => confirmLink("ddt")} disabled={linkBusy || linkSelectedIds.length === 0}>
                {linkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                Collega {linkSelectedIds.length > 0 ? `(${linkSelectedIds.length})` : ""}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VociTable({ voci, isAdmin, isAdminOrCapo, onEdit, onDelete, onSetPct, selected = [], onToggle, onSetFatto }) {
  if (voci.length === 0) {
    return (
      <Card className="card-elevated"><CardContent className="p-10 text-center text-muted-foreground">
        <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-50" />
        Nessuna voce. Carica un computo metrico o aggiungi manualmente.
      </CardContent></Card>
    );
  }
  const allSelected = voci.length > 0 && voci.every((v) => selected.includes(v.id));
  const toggleAll = () => { voci.forEach((v) => { if (allSelected ? selected.includes(v.id) : !selected.includes(v.id)) onToggle?.(v.id); }); };
  return (
    <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary">
          <tr className="text-left">
            {isAdmin && <th className="px-3 py-3"><input type="checkbox" data-testid="voci-sel-all" checked={allSelected} onChange={toggleAll} /></th>}
            <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</th>
            <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">UM</th>
            <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Previsto</th>
            <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Fatto {isAdmin && "(editabile)"}</th>
            <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground w-56">Avanzamento {isAdmin && "(editabile)"}</th>
            {isAdmin && <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">€/UM</th>}
            {isAdmin && <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Realizzato</th>}
            {isAdmin && <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Totale</th>}
            {isAdmin && <th className="px-4 py-3"></th>}
          </tr>
        </thead>
        <tbody>
          {voci.map((v) => (
            <VoceRow key={v.id} v={v} isAdmin={isAdmin} isAdminOrCapo={isAdminOrCapo} onEdit={onEdit} onDelete={onDelete} onSetPct={onSetPct}
                     selected={selected.includes(v.id)} onToggle={onToggle} onSetFatto={onSetFatto} />
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function VoceRow({ v, isAdmin, isAdminOrCapo, onEdit, onDelete, onSetPct, selected, onToggle, onSetFatto }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(v.percentuale || 0);
  const [editFatto, setEditFatto] = useState(false);
  const [fattoVal, setFattoVal] = useState(v.quantita_fatta || 0);
  const save = async () => {
    const n = Math.max(0, Math.min(100, Number(val)));
    await onSetPct?.(v.id, n);
    setEditing(false);
  };
  const saveFatto = async () => {
    await onSetFatto?.(v.id, fattoVal, v.quantita_prevista);
    setEditFatto(false);
  };
  return (
    <tr className="border-t border-border">
      {isAdmin && <td className="px-3 py-3"><input type="checkbox" data-testid={`voci-sel-${v.id}`} checked={!!selected} onChange={() => onToggle?.(v.id)} /></td>}
      <td className="px-4 py-3">
        <div className="font-medium">{v.descrizione}</div>
        {v.categoria && <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{v.categoria}</div>}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{v.unita_misura}</td>
      <td className="px-4 py-3 tabular-nums">{v.quantita_prevista}</td>
      <td className="px-4 py-3 tabular-nums font-semibold text-emerald-700">
        {isAdmin && editFatto ? (
          <div className="flex items-center gap-1">
            <Input data-testid={`v-fatto-input-${v.id}`} type="number" step="0.01" min="0" max={v.quantita_prevista} value={fattoVal}
                   onChange={(e) => setFattoVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveFatto()}
                   className="h-7 w-24 text-xs" autoFocus />
            <button data-testid={`v-fatto-save-${v.id}`} onClick={saveFatto} className="text-primary"><Check className="h-4 w-4" /></button>
            <button onClick={() => { setFattoVal(v.quantita_fatta || 0); setEditFatto(false); }} className="text-muted-foreground"><X className="h-3 w-3" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span>{v.quantita_fatta}</span>
            {isAdmin && <button data-testid={`v-fatto-edit-${v.id}`} onClick={() => { setFattoVal(v.quantita_fatta || 0); setEditFatto(true); }} className="text-muted-foreground hover:text-primary"><Pencil className="h-3 w-3" /></button>}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {editing && isAdmin ? (
          <div className="flex items-center gap-2">
            <Input data-testid={`v-pct-input-${v.id}`} type="number" min="0" max="100" step="1" value={val}
                   onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                   className="h-7 w-20 text-xs" autoFocus />
            <button data-testid={`v-pct-save-${v.id}`} onClick={save} className="text-primary hover:opacity-80"><Check className="h-4 w-4" /></button>
            <button onClick={() => { setVal(v.percentuale || 0); setEditing(false); }} className="text-muted-foreground"><X className="h-3 w-3" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Progress value={v.percentuale} className="h-2 flex-1" />
            <span className="text-xs font-bold w-10 text-right tabular-nums">{v.percentuale}%</span>
            {isAdmin && (
              <button data-testid={`v-pct-edit-${v.id}`} onClick={() => { setVal(v.percentuale || 0); setEditing(true); }} className="text-muted-foreground hover:text-primary">
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </td>
      {isAdmin && <td className="px-4 py-3 tabular-nums">{fmt(v.prezzo_unitario)}</td>}
      {isAdmin && <td className="px-4 py-3 tabular-nums font-bold text-primary">{fmt(v.importo_realizzato)}</td>}
      {isAdmin && <td className="px-4 py-3 tabular-nums font-bold" data-testid={`v-totale-${v.id}`}>{fmt((Number(v.quantita_prevista) || 0) * (Number(v.prezzo_unitario) || 0))}</td>}
      {isAdmin && (
        <td className="px-4 py-3 flex gap-1 justify-end">
          <Button variant="ghost" size="icon" onClick={() => onEdit(v)} data-testid={`edit-v-${v.id}`}><Pencil className="h-3 w-3" /></Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(v.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
        </td>
      )}
    </tr>
  );
}
